import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@progress/kendo-react-dialogs';
import api from '@utils/axios';
import { Button } from '@ui';
import { apiInternalRecurrencesPath, apiInternalRecurrencePath } from 'routes';
import I18n, { translate } from '@utils/i18n';
import DatePickerWoYear from './DatePickerWoYear';
import DayOfMonthSelection from './DayOfMonthSelection';

const namespace = 'features.invoices.recurrence_modal';

const dayEnumValues = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Reorder day names to start with Monday
const dayNames = I18n.t('date.day_names');
const mondayFirstDayNames = [...dayNames.slice(1), dayNames[0]];
const daysOfWeek = mondayFirstDayNames.map((label, index) => ({ label, value: dayEnumValues[index] }));

function getInitialDayOfMonthOption(dayOfMonth) {
  if (dayOfMonth === -1) return 'last_day';
  if (dayOfMonth >= 2 && dayOfMonth <= 28) return 'custom_day';
  return 'first_day';
}

function getInitialAnnualDate(dayOfMonth, monthOfYear) {
  if (dayOfMonth != null && monthOfYear != null) {
    return new Date(new Date().getFullYear(), monthOfYear - 1, dayOfMonth);
  }

  return new Date(); // Default to current date if no date is set
}

function RecurrenceModal({ isVisible, initialRecurrence, invoiceId, isToggleDisabled }) {
  const [visible, setVisible] = useState(isVisible);
  const [formData, setFormData] = useState(initialRecurrence);
  const [active, setActive] = useState(isVisible || (initialRecurrence.id && initialRecurrence.active));
  const [autoSend, setAutoSend] = useState(initialRecurrence?.auto_send || false);

  const id = initialRecurrence?.id;
  const [frequency, setFrequency] = useState(initialRecurrence?.frequency);
  const [dayOfWeek, setDayOfWeek] = useState(initialRecurrence?.day_of_week || 'monday');
  const [dayOfMonth, setDayOfMonth] = useState(initialRecurrence?.day_of_month || 1);
  const [dayOfMonthOption, setDayOfMonthOption] = useState(getInitialDayOfMonthOption(initialRecurrence?.day_of_month));
  const [endType, setEndType] = useState(initialRecurrence?.recurrence_end_type);
  const [maxOccurrences, setMaxOccurrences] = useState(initialRecurrence?.max_occurrences || 1);
  const [endDate, setEndDate] = useState(initialRecurrence?.end_date || new Date());
  const [annualDate, setAnnualDate] = useState(
    getInitialAnnualDate(initialRecurrence?.day_of_month, initialRecurrence?.month_of_year)
  );
  const isSaveDisabled = !frequency;

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const handleDayOfMonthChange = (day) => {
    setDayOfMonth(day);
    if (day >= 2 && day <= 28) {
      setDayOfMonthOption('custom_day');
    }
  };

  const handleDayOfMonthOptionChange = (option) => {
    setDayOfMonthOption(option);

    if (option === 'first_day') {
      setDayOfMonth(1);
    } else if (option === 'last_day') {
      setDayOfMonth(-1);
    } else if (option === 'custom_day' && (!dayOfMonth || dayOfMonth === -1 || dayOfMonth === 1)) {
      setDayOfMonth(2);
    }
  };

  const submitRequest = (newFormData) => {
    if (id) return api.put(apiInternalRecurrencePath(id), { recurrence: newFormData, invoice_id: invoiceId });

    return api.post(apiInternalRecurrencesPath(), { recurrence: newFormData, invoice_id: invoiceId });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let finalDayOfMonth;
    if (frequency === 'monthly' || frequency === 'quarterly' || frequency === 'semi_annually') {
      finalDayOfMonth = dayOfMonth;
    } else if (frequency === 'annually') {
      finalDayOfMonth = annualDate.getDate();
    } else {
      finalDayOfMonth = null;
    }

    const newFormData = {
      id,
      frequency,
      day_of_week: frequency === 'weekly' ? dayOfWeek : null,
      day_of_month: finalDayOfMonth,
      month_of_year: frequency === 'annually' ? annualDate.getMonth() + 1 : null,
      recurrence_end_type: endType,
      max_occurrences: endType === 'after_occurrences' ? maxOccurrences : null,
      end_date: endType === 'on_date' ? endDate : null,
      auto_send: autoSend,
      generate_at_once: false
    };

    if (!invoiceId) {
      setFormData(newFormData);
      toggleDialog();
      return;
    }

    submitRequest(newFormData)
      .then(({ data }) => {
        setFormData(data);
      })
      .catch((error) => {
        console.error('Failed to save recurrence:', error);
      });

    toggleDialog();
  };

  const handleAnnuallyChange = (event) => {
    setAnnualDate(event.value);
  };

  const onCancel = () => {
    if (!formData.id) {
      setActive(false);
    }

    setFrequency(formData.frequency);
    setDayOfWeek(formData.day_of_week || 'monday');
    setDayOfMonth(formData.day_of_month || 1);
    if (formData.day_of_month === 1) {
      setDayOfMonthOption('first_day');
    } else if (formData.day_of_month === -1) {
      setDayOfMonthOption('last_day');
    } else if (formData.day_of_month >= 2 && formData.day_of_month <= 28) {
      setDayOfMonthOption('custom_day');
    } else {
      setDayOfMonthOption('first_day');
    }

    setAnnualDate(getInitialAnnualDate(formData.day_of_month, formData.month_of_year));
    setEndType(formData.recurrence_end_type);
    setMaxOccurrences(formData.max_occurrences || 1);
    setEndDate(formData.end_date || new Date());
    toggleDialog();
  };

  const toggleActive = () => {
    if (!invoiceId) {
      if (!active) setVisible(true);
      setActive(!active);
      return;
    }

    if (active && id) {
      submitRequest({ id, active: false }).then(({ data }) => {
        setFormData(data);
        setActive(false);
      });
    } else if (active) {
      setActive(false);
    } else if (id) {
      submitRequest({ id, active: true }).then(({ data }) => {
        setFormData(data);
        setActive(true);
        setVisible(true);
      });
    } else {
      setActive(true);
      setVisible(true);
    }
  };

  return (
    <>
      <span className="qmb-toggle-switch--check">
        <input
          type="checkbox"
          id="active_toggle"
          disabled={isToggleDisabled}
          checked={active}
          onChange={toggleActive}
        />
        <label className="switch__visual" htmlFor="active_toggle">
          &nbsp;
        </label>
        <label className="qmb-label" htmlFor="active_toggle">
          {translate('recurring_invoice', { namespace })}
        </label>
      </span>

      {active && <Button onClick={toggleDialog}>{translate('edit_recurring_invoice', { namespace })}</Button>}

      {!invoiceId &&
        formData &&
        active &&
        Object.entries(formData).map(([name, datum]) => (
          <input key={name} type="hidden" name={`recurrence[${name}]`} value={datum} />
        ))}

      {visible && (
        <Dialog
          title={translate('title', { namespace })}
          className="qmb-dialog"
          onClose={onCancel}
          width={600}
          autoFocus
          focusTrapped
          id="recurrence-modal-title"
          aria-modal="true">
          <form onSubmit={handleSubmit} aria-labelledby="recurrence-modal-title">
            <h4>{translate('when', { namespace })}</h4>
            <div>
              <label className="qmb-radio">
                <input
                  type="radio"
                  name="frequency"
                  value="daily"
                  checked={frequency === 'daily'}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                {translate('daily', { namespace })}
              </label>
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <label className="qmb-radio">
                  <input
                    type="radio"
                    name="frequency"
                    value="weekly"
                    checked={frequency === 'weekly'}
                    onChange={(e) => setFrequency(e.target.value)}
                  />
                  {translate('weekly_on', { namespace })}
                </label>
                &nbsp;
                {frequency === 'weekly' && (
                  <div className="qmb-select--blank" style={{ width: 'initial' }}>
                    <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
                      {daysOfWeek.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <label className="qmb-radio">
                <input
                  type="radio"
                  name="frequency"
                  value="monthly"
                  checked={frequency === 'monthly'}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                {translate('monthly_on', { namespace })}
              </label>
              {frequency === 'monthly' && (
                <DayOfMonthSelection
                  dayOfMonthOption={dayOfMonthOption}
                  dayOfMonth={dayOfMonth}
                  onOptionChange={handleDayOfMonthOptionChange}
                  onDayChange={handleDayOfMonthChange}
                />
              )}
              <label className="qmb-radio">
                <input
                  type="radio"
                  name="frequency"
                  value="quarterly"
                  checked={frequency === 'quarterly'}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                {translate('quarterly_on', { namespace })}{' '}
                <span style={{ color: '#666', fontSize: '0.9em' }}>{translate('quarterly_help', { namespace })}</span>
              </label>
              {frequency === 'quarterly' && (
                <DayOfMonthSelection
                  dayOfMonthOption={dayOfMonthOption}
                  dayOfMonth={dayOfMonth}
                  onOptionChange={handleDayOfMonthOptionChange}
                  onDayChange={handleDayOfMonthChange}
                />
              )}
              <label className="qmb-radio">
                <input
                  type="radio"
                  name="frequency"
                  value="semi_annually"
                  checked={frequency === 'semi_annually'}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                {translate('semi_annually_on', { namespace })}{' '}
                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  {translate('semi_annually_help', { namespace })}
                </span>
              </label>
              {frequency === 'semi_annually' && (
                <DayOfMonthSelection
                  dayOfMonthOption={dayOfMonthOption}
                  dayOfMonth={dayOfMonth}
                  onOptionChange={handleDayOfMonthOptionChange}
                  onDayChange={handleDayOfMonthChange}
                />
              )}
              <label className="qmb-radio">
                <input
                  type="radio"
                  name="frequency"
                  value="annually"
                  checked={frequency === 'annually'}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                {translate('yearly_on', { namespace })}
              </label>
              {frequency === 'annually' && (
                <DatePickerWoYear
                  value={annualDate}
                  min={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                  max={new Date(new Date().getFullYear(), new Date().getMonth() + 13, 0)}
                  onChange={handleAnnuallyChange}
                  hideHeaderTitle
                />
              )}
            </div>

            <br />
            <h4>{translate('when_should_recurrence_end', { namespace })}</h4>
            <label className="qmb-radio">
              <input
                type="radio"
                name="end_type"
                value="after_occurrences"
                checked={endType === 'after_occurrences'}
                onChange={(e) => setEndType(e.target.value)}
              />
              {translate('after', { namespace })}
            </label>
            {endType === 'after_occurrences' && (
              <div className="d-inline-flex align-items-center">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={maxOccurrences}
                  onChange={(e) => setMaxOccurrences(e.target.value)}
                  className="form-control form-control-sm mx-2"
                  style={{ width: '70px' }}
                />
                <span>{translate('occurrences', { namespace })}</span>
              </div>
            )}
            <label className="qmb-radio">
              <input
                type="radio"
                name="end_type"
                value="on_date"
                checked={endType === 'on_date'}
                onChange={(e) => setEndType(e.target.value)}
              />
              {translate('onn', { namespace })}
            </label>
            {endType === 'on_date' && (
              <DatePickerWoYear
                min={new Date()}
                value={new Date(endDate)}
                onChange={(event) => setEndDate(event.value.toISOString().split('T')[0])}
              />
            )}
            <label className="qmb-radio">
              <input
                type="radio"
                name="end_type"
                value="never"
                checked={endType === 'never'}
                onChange={(e) => setEndType(e.target.value)}
              />
              {translate('never', { namespace })}
            </label>

            <br />
            <hr />
            <br />

            <div>
              <label className="qmb-checkbox">
                <input
                  type="checkbox"
                  checked={autoSend}
                  onChange={() => setAutoSend(!autoSend)}
                />
                {translate('auto_send_invoice', { namespace })}
              </label>
            </div>

            <br />
            <div className="modal__footer pull-right">
              <Button onClick={onCancel}>{I18n.t('generic.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={isSaveDisabled}>
                {I18n.t('generic.save')}
              </Button>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

RecurrenceModal.propTypes = {
  isToggleDisabled: PropTypes.bool,
  isVisible: PropTypes.bool,
  invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialRecurrence: PropTypes.shape({
    id: PropTypes.number,
    active: PropTypes.bool,
    frequency: PropTypes.oneOf(['daily', 'weekly', 'monthly', 'quarterly', 'semi_annually', 'annually']),
    day_of_week: PropTypes.oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']),
    day_of_month: PropTypes.number,
    month_of_year: PropTypes.number,
    recurrence_end_type: PropTypes.oneOf(['never', 'on_date', 'after_occurrences']),
    max_occurrences: PropTypes.number,
    end_date: PropTypes.string,
    auto_send: PropTypes.bool
  })
};

RecurrenceModal.defaultProps = {
  isToggleDisabled: false,
  isVisible: false,
  invoiceId: null,
  initialRecurrence: {}
};

export default RecurrenceModal;
